import { ParamMap } from '@angular/router';
import { IMethod } from '../models/structure/components/basic';

/** Query-parameter name the backend appends to links that lead out of a running process (e.g. checkout). */
const RETURN_URL_PARAM = 'returnurl';

/**
 * Local paths only, mirroring `UrlSecurity.IsSafeLocalUrl` in Projects3: a leading slash, but neither
 * `//host` nor `/\host`, which browsers resolve to a foreign origin.
 */
const LOCAL_PATH = /^\/(?![/\\])/;

/**
 * Whitespace browsers strip before parsing a URL, turning `/\t/host` into `//host` — so a value
 * carrying it is rejected outright, as in `UrlSecurity.IsSafeLocalUrl`.
 */
const STRIPPED_WHITESPACE = /[\t\r\n]/;

/**
 * Reads the return URL of the process the user came from (checkout/inquiry) and maps it onto the
 * parameter the given backend method expects, so login/register can redirect back into that process.
 * The backend decides whether the return URL wins — the "Weiterleitung forcieren" setting can discard it.
 *
 * Only local paths are forwarded, mirroring `UrlSecurity.IsSafeLocalUrl` in Projects3, which is the
 * authoritative check. Methods without a return-URL parameter yield `null`.
 *
 * @param method the backend method the parameter is passed to (its save call)
 * @param queryParams the query parameters of the current route
 * @returns the url parameters for the method call, or null when there is nothing to forward
 */
export function returnUrlParams(method: IMethod | undefined, queryParams: ParamMap): Record<string, string> | null {
  const returnUrl = queryParams.get(RETURN_URL_PARAM) ?? queryParams.get('returnUrl');
  if (!returnUrl || STRIPPED_WHITESPACE.test(returnUrl) || !LOCAL_PATH.test(returnUrl)) return null;

  const parameterName = method?.parameters?.find((parameter) => parameter.name.toLowerCase() === RETURN_URL_PARAM)?.name;
  return parameterName ? { [parameterName]: returnUrl } : null;
}
